import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as redux from '../kb-redux';
import { Apple } from 'assets/icons/Icons';
import { Button } from 'components/v2/Buttons/Button/Button';
import { utils } from 'kb-shared';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { analytics } from 'utilities/analytics';
import { isOnline } from 'utilities/browser';

import AppleDisclaimerModal from './Apple/AppleDisclaimerModal';
import AppleNotLoadedModal from './Apple/AppleNotLoadedModal';

const { VITE_APP_APPLE_CLIENT_ID, VITE_APP_APPLE_REDIRECT_URL } = import.meta.env;

const { signInAppleUser } = redux.patient;
const { decodeJwtPayload } = utils;

const APPLE_SIGN_IN_SCRIPT =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const APPLE_SCRIPT_LOAD_TIMEOUT_MS = 2000;

type AppleOperation = 'login' | 'signUp';

interface Props {
  operation: AppleOperation;
}

interface AppleSignInErrorParams {
  action: string;
  operation: AppleOperation;
  errorCode?: string;
  errorMessage?: string;
}

const getAppleSignInScriptElement = () =>
  window.document.querySelector<HTMLScriptElement>(`script[src="${APPLE_SIGN_IN_SCRIPT}"]`);

const notifyAppleSignInError = ({
  action,
  operation,
  errorCode,
  errorMessage
}: AppleSignInErrorParams) => {
  const scriptElement = getAppleSignInScriptElement();

  BugTracker.notify({
    name: 'Apple SignIn',
    message: errorMessage || 'Apple sign-in failed',
    context: {
      action,
      operation,
      errorCode,
      online: isOnline(),
      userAgent: window.navigator.userAgent,
      hasAppleID: Boolean(window.AppleID),
      hasAppleAuth: Boolean(window.AppleID?.auth),
      scriptEndpoint: APPLE_SIGN_IN_SCRIPT,
      scriptPresent: Boolean(scriptElement),
      scriptCrossOrigin: scriptElement?.crossOrigin
    },
    tags: ['apple-sso'],
    component: 'AppleAuthButton.tsx',
    action
  });
};

const initAppleSignIn = (operation: AppleOperation) => {
  if (!window.AppleID?.auth) return false;

  try {
    window.AppleID.auth.init({
      clientId: VITE_APP_APPLE_CLIENT_ID, // This is the service ID we created.
      scope: 'name email', // To tell apple we want the user name and emails fields in the response it sends us.
      redirectURI: VITE_APP_APPLE_REDIRECT_URL, // As registered along with our service ID
      state: 'origin:web', // Any string of your choice that you may use for some logic. It's optional and you may omit it.
      usePopup: true // Important if we want to capture the data apple sends on the client side.
    });

    return true;
  } catch (error) {
    notifyAppleSignInError({
      action: 'apple-auth-init-failed',
      operation,
      errorCode: 'apple_auth_init_error',
      errorMessage: error instanceof Error ? error.message : 'Failed to initialize Apple auth'
    });
    return false;
  }
};

const AppleAuthButton = (props: Props) => {
  const dispatch = useDispatch();
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showNotLoadedModal, setShowNotLoadedModal] = useState(false);
  const hasReportedScriptError = useRef(false);

  const processAppleErrorCode = useCallback(
    (errorCode?: string, error?: unknown) => {
      if (
        !errorCode ||
        errorCode === 'popup_closed_by_user' ||
        errorCode === 'user_cancelled_authorize' ||
        errorCode === 'user_trigger_new_signin_flow'
      ) {
        return;
      }

      notifyAppleSignInError({
        action: 'apple-sign-in-flow-failed',
        operation: props.operation,
        errorCode,
        errorMessage: error instanceof Error ? error.message : undefined
      });
    },
    [props.operation]
  );

  const reportAppleScriptLoadFailure = useCallback(
    (action: string) => {
      if (hasReportedScriptError.current) return;
      hasReportedScriptError.current = true;

      if (!isOnline()) {
        notifyAppleSignInError({
          action,
          operation: props.operation,
          errorCode: 'offline'
        });
        return;
      }

      fetch(APPLE_SIGN_IN_SCRIPT)
        .then(() => {
          notifyAppleSignInError({
            action,
            operation: props.operation,
            errorCode: 'apple_object_missing',
            errorMessage: 'Apple sign-in script fetched but AppleID was unavailable'
          });
        })
        .catch(error => {
          notifyAppleSignInError({
            action: `${action}-fetch-failed`,
            operation: props.operation,
            errorCode: 'apple_script_fetch_failed',
            errorMessage:
              error instanceof Error ? error.message : 'Failed to fetch Apple sign-in script'
          });
        });
    },
    [props.operation]
  );

  const handleSignInFailure = useCallback(
    (event: Event) => {
      const signInFailureEvent = event as Event & { detail?: { error?: string } };
      processAppleErrorCode(signInFailureEvent?.detail?.error);
    },
    [processAppleErrorCode]
  );

  useEffect(() => {
    const scriptElement = getAppleSignInScriptElement();
    const handleScriptError = () => reportAppleScriptLoadFailure('apple-script-error-event');

    scriptElement?.addEventListener('error', handleScriptError);
    window.document.addEventListener('AppleIDSignInOnFailure', handleSignInFailure);

    let retryTimeout: number | undefined;

    if (!initAppleSignIn(props.operation)) {
      retryTimeout = window.setTimeout(() => {
        if (!initAppleSignIn(props.operation)) {
          reportAppleScriptLoadFailure('apple-script-load-timeout');
        }
      }, APPLE_SCRIPT_LOAD_TIMEOUT_MS);
    }

    return () => {
      scriptElement?.removeEventListener('error', handleScriptError);
      window.document.removeEventListener('AppleIDSignInOnFailure', handleSignInFailure);

      if (retryTimeout) {
        window.clearTimeout(retryTimeout);
      }
    };
  }, [handleSignInFailure, props.operation, reportAppleScriptLoadFailure]);

  const signInApple = async () => {
    try {
      // response documentation https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js/configuring_your_webpage_for_sign_in_with_apple
      const { authorization, user } = await window.AppleID.auth.signIn();
      window?.sessionStorage?.setItem('@AppleSSO:idToken', authorization.id_token as string);
      const jsonPayload = decodeJwtPayload(authorization.id_token);
      const firstName = user?.name?.firstName;
      const lastName = user?.name?.lastName;
      analytics.track(analytics.EVENTS.APPLE_SIGN_IN_SUCCEEDED);
      dispatch(
        signInAppleUser(
          authorization.id_token,
          jsonPayload.email,
          props.operation,
          firstName,
          lastName
        )
      );
    } catch (error) {
      const appleError = error as { error?: string };
      processAppleErrorCode(appleError?.error, error);
    }
  };

  const handleClick = () => {
    analytics.track(analytics.EVENTS.APPLE_SIGN_IN_STARTED);

    if (!window.AppleID) {
      reportAppleScriptLoadFailure('apple-script-missing-on-click');
      setShowNotLoadedModal(true);
      return;
    }

    if (props.operation === 'login') {
      return signInApple();
    }

    setShowDisclaimerModal(true);
  };

  const handleModalClose = () => {
    setShowDisclaimerModal(false);
    signInApple();
  };

  const handleShowLoadedModalClose = () => {
    window.location.reload();
  };

  return (
    <>
      <Button
        label="Continue with Apple"
        category="primary-dark"
        size="sm"
        fullWidth
        leftIcon={<Apple ariaHidden />}
        onClick={handleClick}
      />
      <AppleDisclaimerModal showModal={showDisclaimerModal} handleClose={handleModalClose} />
      <AppleNotLoadedModal
        showModal={showNotLoadedModal}
        handleClose={handleShowLoadedModalClose}
      />
    </>
  );
};

export default AppleAuthButton;
